import React from 'react';
import { useTranslation } from 'react-i18next';

import { NOTIF_TYPES } from '@private/notifications';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import api from 'api';
import { TRANSACTIONS_KEYS } from 'api/transactions/constants';
import {
  IChargebackStatistics,
  IChargebackStatisticsData,
  IReconciliationForm,
  IRefundTransactionPayload,
  ITransaction,
  ITransactionCard,
  ITransactionCascading,
  ITransactionCustomer,
  ITransactionDetails,
  ITransactionEditData,
  ITransactionOperationHistory,
  ITransactionRoutingHistory,
  ITransactionWebhooks,
} from 'api/transactions/types';
import { STATUSES } from 'constants/common';
import { normalizeQueryKey, useGetAllEntities } from 'queries/utils';
import { getQueryBoundary } from 'utils/common';
import { notificationService } from 'utils/notificationService';
import {
  IPageType,
  IRedirectLinkResponse,
  IUseGetGridData,
  QueryError,
} from 'utils/types';

interface IUseGetTransactions extends IUseGetGridData {
  onlyMain?: boolean;
}

export const useGetTransactions = ({
  additionalKeys = [],
  onlyMain = false,
  pageFilters,
}: IUseGetTransactions): UseQueryResult<IPageType<ITransaction>> => {
  const queryDeps = React.useMemo(() => [onlyMain], [onlyMain]);

  return useGetAllEntities<ITransaction>({
    getAllEntitiesFunc: (filters) =>
      api.transactions.getAllTransactions(filters, onlyMain),
    pageFilters,
    queryDeps,
    queryKey: [...additionalKeys, TRANSACTIONS_KEYS.ALL],
  });
};

export const useGetTransactionDetails = (
  id?: string,
): UseQueryResult<ITransactionDetails> => {
  const queryFn = React.useCallback(
    () => (id ? api.transactions.getTransactionDetails(id) : null),
    [id],
  );

  return useQuery({
    queryFn,
    queryKey: normalizeQueryKey([TRANSACTIONS_KEYS.TRANSACTION_DETAILS, id]),
  });
};

export const useGetTransactionCard = (id?: string): UseQueryResult<ITransactionCard> => {
  const queryFn = React.useCallback(
    () => (id ? api.transactions.getTransactionCard(id) : null),
    [id],
  );

  return useQuery({
    queryFn,
    queryKey: normalizeQueryKey([TRANSACTIONS_KEYS.TRANSACTION_CARD, id]),
  });
};

export const useGetTransactionCustomer = (
  id?: string,
): UseQueryResult<ITransactionCustomer> => {
  const queryFn = React.useCallback(
    () => (id ? api.transactions.getTransactionCustomer(id) : null),
    [id],
  );

  return useQuery({
    queryFn,
    queryKey: normalizeQueryKey([TRANSACTIONS_KEYS.TRANSACTION_CUSTOMER, id]),
  });
};

export const useGetTransactionOperationHistory = (
  id: string,
): UseQueryResult<ITransactionOperationHistory> => {
  const queryFn = React.useCallback(
    () => (id ? api.transactions.getTransactionOperationHistory(id) : null),
    [id],
  );

  return useQuery({
    queryFn,
    queryKey: normalizeQueryKey([TRANSACTIONS_KEYS.OPERATION_HISTORY, id]),
  });
};

export const useRefundTransaction = (
  id: string,
): UseMutationResult<IRedirectLinkResponse, QueryError, IRefundTransactionPayload> => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const mutationFn = ({ id, ...payload }: IRefundTransactionPayload) =>
    api.transactions.refundTransaction(id, payload);

  const toastId = 'refundTransaction';
  const ntfPrefix = 'transaction.refundModal.notifications';

  return useMutation({
    mutationFn,
    onError: (error: QueryError) => {
      const isSavingError = error?.response?.status === STATUSES.INVALID_DATA;
      const errorsExists = Object.keys(error?.response?.data?.errors || {}).length;

      if (errorsExists) {
        notificationService.show({
          message:
            error?.response?.data?.message ||
            (isSavingError ? t('notifications.invalidData') : t(`${ntfPrefix}.error`)),
          title: t('notifications.errorTitle'),
          type: NOTIF_TYPES.ERROR,
          updateId: toastId,
        });
      }
    },
    onMutate: () => {
      notificationService.show({
        message: t(`${ntfPrefix}.loading`),
        title: t('notifications.loadingTitle'),
        toastId,
        type: NOTIF_TYPES.LOADING,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([TRANSACTIONS_KEYS.TRANSACTION_DETAILS, id]),
      });

      notificationService.show({
        message: t(`${ntfPrefix}.success`),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: toastId,
      });
    },
  });
};

export const useApprovePayoutTransaction = (): UseMutationResult<
  IRedirectLinkResponse,
  QueryError,
  string
> => {
  const { t } = useTranslation();

  const mutationFn = (id: string) => api.transactions.approvePayoutTransaction(id);

  const toastId = 'approvePayoutTransaction';
  const ntfPrefix = 'transaction.baseDetails.approvePayout.notifications';

  return useMutation({
    mutationFn,
    onError: (error: QueryError) => {
      const errorsExists = Object.keys(error?.response?.data?.errors || {}).length;

      if (errorsExists) {
        notificationService.show({
          message: error?.response?.data?.message || t(`${ntfPrefix}.error`),
          title: t('notifications.errorTitle'),
          type: NOTIF_TYPES.ERROR,
          updateId: toastId,
        });
      }
    },
    onMutate: () => {
      notificationService.show({
        message: t(`${ntfPrefix}.loading`),
        title: t('notifications.loadingTitle'),
        toastId,
        type: NOTIF_TYPES.LOADING,
      });
    },
    onSuccess: () => {
      notificationService.show({
        message: t(`${ntfPrefix}.success`),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: toastId,
      });
    },
  });
};

export const useDeclinePayoutTransaction = (): UseMutationResult<
  IRedirectLinkResponse,
  QueryError,
  string
> => {
  const { t } = useTranslation();

  const mutationFn = (id: string) => api.transactions.declinePayoutTransaction(id);

  const toastId = 'approvePayoutTransaction';
  const ntfPrefix = 'transaction.baseDetails.declinePayout.notifications';

  return useMutation({
    mutationFn,
    onError: (error: QueryError) => {
      const errorsExists = Object.keys(error?.response?.data?.errors || {}).length;

      if (errorsExists) {
        notificationService.show({
          message: error?.response?.data?.message || t(`${ntfPrefix}.error`),
          title: t('notifications.errorTitle'),
          type: NOTIF_TYPES.ERROR,
          updateId: toastId,
        });
      }
    },
    onMutate: () => {
      notificationService.show({
        message: t(`${ntfPrefix}.loading`),
        title: t('notifications.loadingTitle'),
        toastId,
        type: NOTIF_TYPES.LOADING,
      });
    },
    onSuccess: () => {
      notificationService.show({
        message: t(`${ntfPrefix}.success`),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: toastId,
      });
    },
  });
};

export const useUpdateTransactionState = (id: string) => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const mutationFn = (payload: ITransactionEditData) =>
    api.transactions.updateTransaction(id, payload);

  return useMutation({
    mutationFn,
    onError: (error: QueryError, _variables, context) => {
      const isSavingError = error?.response?.status === STATUSES.INVALID_DATA;
      const errorsExists = Object.keys(error?.response?.data?.errors || {}).length;

      if (errorsExists) {
        notificationService.show({
          message:
            error?.response?.data?.message ||
            (isSavingError
              ? t('notifications.invalidData')
              : t('transaction.notifications.edit.error')),
          title: t('notifications.errorTitle'),
          type: NOTIF_TYPES.ERROR,
          updateId: context?.ntfID,
        });
      } else {
        notificationService.remove(context?.ntfID || '');
      }
    },
    onMutate: () => {
      const ntfID = notificationService.show({
        message: t('transaction.notifications.edit.loading'),
        title: t('notifications.loadingTitle'),
        type: NOTIF_TYPES.LOADING,
      });

      return { ntfID };
    },
    onSuccess: (_payload, _, context) => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([TRANSACTIONS_KEYS.TRANSACTION_DETAILS, id]),
      });
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([TRANSACTIONS_KEYS.TRANSACTION_CUSTOMER, id]),
      });

      notificationService.show({
        message: t('transaction.notifications.edit.success'),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: context?.ntfID,
      });
    },
  });
};

export const useGetTransactionWebhooks = (
  id?: string,
): UseQueryResult<ITransactionWebhooks> => {
  const queryFn = React.useCallback(
    () => (id ? api.transactions.getTransactionWebhooks(id) : null),
    [id],
  );

  return useQuery({
    queryFn,
    queryKey: normalizeQueryKey([TRANSACTIONS_KEYS.WEBHOOKS, id]),
  });
};

export const useGetTransactionCascading = (
  id?: string,
): UseQueryResult<ITransactionCascading> => {
  const queryFn = React.useCallback(
    () => (id ? api.transactions.getTransactionCascading(id) : null),
    [id],
  );

  return useQuery({
    queryFn,
    queryKey: normalizeQueryKey([TRANSACTIONS_KEYS.CASCADING, id]),
  });
};

export const useGetTransactionRoutingHistory = (
  id?: string,
): UseQueryResult<ITransactionRoutingHistory> => {
  const queryFn = React.useCallback(
    () => (id ? api.transactions.getTransactionRoutingHistory(id) : null),
    [id],
  );

  return useQuery({
    queryFn,
    queryKey: normalizeQueryKey([TRANSACTIONS_KEYS.ROUTING_HISTORY, id]),
  });
};

export const useGetChargebackStatistics = (
  params: IChargebackStatisticsData,
): UseQueryResult<IChargebackStatistics> => {
  const queryFn = React.useCallback(
    () => (params ? api.transactions.getChargebackStatistics(params) : null),
    [params],
  );

  return useQuery({
    queryFn,
    queryKey: normalizeQueryKey([
      TRANSACTIONS_KEYS.CHARGEBACK_STATISTICS,
      params.merchantTerminalId,
      params.reportingPeriodFrom,
      params.reportingPeriodTo,
    ]),
    staleTime: 0,
  });
};

export const useReconciliation = (): UseMutationResult<
  AxiosResponse,
  QueryError,
  IReconciliationForm
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const toastId = 'reconciliation';
  const ntfPrefix = 'transactions.reconciliation.notifications';

  return useMutation({
    mutationFn: api.transactions.reconciliation,
    onError: (error: QueryError) => {
      notificationService.show({
        message: error?.response?.data?.message || t(`${ntfPrefix}.error`),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
        updateId: toastId,
      });
    },
    onMutate: () => {
      notificationService.show({
        message: t(`${ntfPrefix}.loading`),
        title: t('notifications.loadingTitle'),
        toastId,
        type: NOTIF_TYPES.LOADING,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([TRANSACTIONS_KEYS.ALL]),
      });

      notificationService.show({
        message: t(`${ntfPrefix}.success`),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: toastId,
      });
    },
    throwOnError: (error: QueryError) => getQueryBoundary(error, t, toastId),
  });
};
